// クレジット (2026-10-31 作者「タイトルのメニューに足す」・Spine のライセンス全文を画面に載せる)。
//   Spine Runtimes の許諾は「配る物にこの許諾と著作権表示を含める」が条件 (docs/release/licenses.md §2)。
//   全文は node_modules/@esotericsoftware/spine-core/LICENSE (2025-04-05 改定版) の写し。 版を上げたら写し直す
//   (tests/credits.test.ts が元のファイルとの一致を機械で確かめる)。
//   Electron と steamworks.js は Steam 版だけに入るので、 Steam 版 (vite --mode steam) の時だけ並べる。
//   作った人＝工房名「Mochi Michiya」だけ (11-01 作者「ワークスはつけない」)。本名・呼び名は載せない (10-31 作者「もりしはとくていされるからいや」)。
//   生成 AI の道具も1行ずつ載せる (10-31 作者の答え name_ai・店のページの AI の申告と揃える・道具は docs/release/licenses.md)。

export type CreditLine = { text: string, kind: 'title' | 'heading' | 'name' | 'body' | 'license' | 'gap' }

export const SPINE_LICENSE_TEXT: readonly string[] = [
  "Spine Runtimes License Agreement",
  "Last updated April 5, 2025. Replaces all prior versions.",
  "",
  "Copyright (c) 2013-2025, Esoteric Software LLC",
  "",
  "Integration of the Spine Runtimes into software or otherwise creating",
  "derivative works of the Spine Runtimes is permitted under the terms and",
  "conditions of Section 2 of the Spine Editor License Agreement:",
  "http://esotericsoftware.com/spine-editor-license",
  "",
  "Otherwise, it is permitted to integrate the Spine Runtimes into software",
  "or otherwise create derivative works of the Spine Runtimes (collectively,",
  "\"Products\"), provided that each user of the Products must obtain their own",
  "Spine Editor license and redistribution of the Products in any form must",
  "include this license and copyright notice.",
  "",
  "THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC \"AS IS\" AND ANY",
  "EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED",
  "WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE",
  "DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY",
  "DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES",
  "(INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,",
  "BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND",
  "ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT",
  "(INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF",
  "THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.",
]

const MIT_BODY: readonly string[] = [
  "Permission is hereby granted, free of charge, to any person obtaining",
  "a copy of this software and associated documentation files (the",
  "\"Software\"), to deal in the Software without restriction, including",
  "without limitation the rights to use, copy, modify, merge, publish,",
  "distribute, sublicense, and/or sell copies of the Software, and to",
  "permit persons to whom the Software is furnished to do so, subject to",
  "the following conditions:",
  "",
  "The above copyright notice and this permission notice shall be",
  "included in all copies or substantial portions of the Software.",
  "",
  "THE SOFTWARE IS PROVIDED \"AS IS\", WITHOUT WARRANTY OF ANY KIND,",
  "EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF",
  "MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND",
  "NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE",
  "LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION",
  "OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION",
  "WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.",
]

const ELECTRON_NOTICE: readonly string[] = [
  'Copyright (c) Electron contributors',
  'Copyright (c) 2013-2020 GitHub Inc.',
]

const STEAMWORKS_JS_NOTICE: readonly string[] = [
  'MIT License',
  '',
  'Copyright (c) 2022 Gabriel Francisco Dos Santos',
]

function lic(lines: readonly string[]): CreditLine[] {
  return lines.map((text) => (text === '' ? { text: '', kind: 'gap' } : { text, kind: 'license' }))
}

/** 工房名 (11-01 作者が決めた)。 */
export const STUDIO_NAME = 'Mochi Michiya'

const AI_TOOLS: readonly string[] = [
  '絵 — ChatGPT・Codex (OpenAI)',
  '曲 — Suno',
  '効果音 — ElevenLabs',
  '文の下書き — Claude (Anthropic)',
]

export function creditLines(steam: boolean): CreditLine[] {
  const out: CreditLine[] = [
    { text: 'LUNAIRE', kind: 'title' },
    { text: '', kind: 'gap' },
    { text: '制作', kind: 'heading' },
    { text: STUDIO_NAME, kind: 'name' },
    { text: '', kind: 'gap' },
    { text: '生成 AI の道具', kind: 'heading' },
    ...AI_TOOLS.map((text): CreditLine => ({ text, kind: 'body' })),
    { text: '', kind: 'gap' },
    { text: '使っている部品', kind: 'heading' },
    { text: 'Spine Runtimes — Esoteric Software LLC', kind: 'name' },
    ...lic(SPINE_LICENSE_TEXT),
  ]
  if (steam) {
    out.push(
      { text: '', kind: 'gap' },
      { text: 'Electron', kind: 'name' },
      ...lic(ELECTRON_NOTICE), { text: '', kind: 'gap' }, ...lic(MIT_BODY),
      { text: '', kind: 'gap' },
      { text: 'Chromium など Electron に含まれる部品の表記は、 同梱の LICENSES.chromium.html にあります。', kind: 'body' },
      { text: '', kind: 'gap' },
      { text: 'steamworks.js', kind: 'name' },
      ...lic(STEAMWORKS_JS_NOTICE), { text: '', kind: 'gap' }, ...lic(MIT_BODY),
      { text: '', kind: 'gap' },
      { text: 'Steamworks SDK — Valve Corporation', kind: 'name' },
    )
  }
  return out
}

// ===== 並べ方 (描画 render.ts drawCreditsPanel と 送り・当たり判定 game.ts の共通の出所) =====
export const CREDITS_PANEL = {
  w: 760, h: 470, y: 30,
  padTop: 22, viewH: 386,   // 文を流す窓 (パネル上端 + padTop から viewH の高さ)
  textX: 40,
  scrollStep: 26,           // 上下キー・ホイール1段で送る量 (px)
  buttonW: 150, buttonH: 30, buttonBottom: 16,
} as const

const LINE_H: Record<CreditLine['kind'], number> = { title: 40, heading: 30, name: 24, body: 20, license: 15, gap: 10 }

export function creditLineHeight(kind: CreditLine['kind']): number { return LINE_H[kind] }

export function creditsMaxScroll(lines: readonly CreditLine[]): number {
  const total = lines.reduce((sum, l) => sum + LINE_H[l.kind], 0)
  return Math.max(0, total - CREDITS_PANEL.viewH)
}

export function creditsPanelX(arenaWidth: number): number { return arenaWidth / 2 - CREDITS_PANEL.w / 2 }

export function creditsCloseRect(arenaWidth: number): { x: number, y: number, w: number, h: number } {
  const { h, y, buttonW, buttonH, buttonBottom } = CREDITS_PANEL
  return { x: arenaWidth / 2 - buttonW / 2, y: y + h - buttonBottom - buttonH, w: buttonW, h: buttonH }
}
